import { Component, Input, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import moment from 'moment';
import { BaseDialogComponent } from '../base-dialog/base-dialog.component';
import { BaseComponent } from '../base-form/base-form.component';
import _ from 'lodash';
import { from, concatMap, forkJoin, Observable } from 'rxjs';
import { RecurrenceConfig } from '../standing-series/standing-series.component';
import { StandingSeriesSaveService, StandingSeriesSaveResult, StandingSeriesSaveOptions } from '../../../services/standing-series-save.service';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-event-group-lesson-entity',
  templateUrl: './event-group-lesson-entity.component.html',
  styleUrls: ['./event-group-lesson-entity.component.css']
})
export class EventGroupLessonEntityComponent extends BaseDialogComponent implements OnInit, AfterViewInit {
  override eckType = "events";
  override bundle = "group_lesson";
  override entityId = this._injectedDialogData['data']['EntityID'];

  entityType = "attendees";
  entityBundle = "attendance";

  // Recurring series state. Group lessons generate attendee-less event shells
  // (staff add the roster per occurrence) and can target multiple instructors
  // at once, so the create form produces one standing_series per instructor,
  // same shape as the schedules dialog. The service is built in ngOnInit from
  // the base class's already-injected REST services (Angular 13 NG0203/NG0100
  // pattern; see the lesson/services/schedules dialogs).
  private _seriesSave!: StandingSeriesSaveService;
  isRecurring = false;
  recurrenceConfig: RecurrenceConfig | null = null;
  seriesConflicts: any[] = [];
  pendingSeriesIds: number[] = [];
  private _pendingOptsById = new Map<number, StandingSeriesSaveOptions>();

  override ngOnInit(): void {
    super.ngOnInit();
    // Build the shared save service from the already-injected REST services.
    // Not via inject()/field initializer (Angular 13 NG0203) and not a
    // template-reachable getter (NG0100); see the other event dialogs.
    this._seriesSave = new StandingSeriesSaveService(
      this._entityRESTService,
      this._drupalRESTService,
    );
  }

  // Commit a pending inline attendee on Save so users who forget to click
  // Create attendees / Edit Attendee don't silently lose their changes.
  commitPendingAttendee(): void {
    const value = this.f?.form?.controls?.['__field_students_inline_form']?.value;
    if (!value?.field_student_account || !value?.field_students) { return; }

    if (this.editMode) {
      this.studentUpdateSubmit(this.editIndex, { ...value });
    } else {
      this.studentIncrement({ ...value });
    }
  }

  override onSubmit(form): void {
    this.commitPendingAttendee();

    // Recurring branch: create one standing_series per selected instructor and
    // generate attendee-less group-lesson shells, instead of one-off events.
    if (this.isRecurring && this.recurrenceConfig && this.actionType === 'create') {
      this.saveAsRecurring(form);
      return;
    }

    this.errorMessage = "";
    this.displayProgressSpinner(true);

    let values;
    values = _.cloneDeep(form.value);
    this.__alterValues(values);

    // Handle field_event_reference for attendees
    if (values.field_students && Array.isArray(values.field_students)) {
      values.field_students.forEach((attendee: any) => {
        if (this.entityId) {
          // Edit mode: set the event reference
          attendee.field_event_reference = this.entityId;
        } else {
          // Create mode: remove empty field_event_reference to avoid validation error
          // The backend will not set this, but the field is not required
          delete attendee.field_event_reference;
        }
      });
    }

    // Check for create mode.
    if (this.actionType == "create") {
      this.displayProgressSpinner(true);

      from(values.field_instructor).pipe(
        concatMap(instructorId => {
          const instructorValues = { ...values, field_instructor: instructorId };
          return this._entityRESTService.createEntity(this.eckType, this.bundle, instructorValues);
        })
      ).subscribe({
        next: data => LoggerService.log('data', data),
        error: error => {
          this.handleError(error);
          this.displayProgressSpinner(false);
        },
        complete: () => {
          this.closeDialog();
          this.displayProgressSpinner(false);
        }
      });
    }

    if (this.actionType == "edit") {
      this.displayProgressSpinner(true);

      this._entityRESTService.patchEntity(this.eckType, this.bundle, this.entityId, values)
        .subscribe(data => {
          LoggerService.log('data', data);
          this.closeDialog();
          this.displayProgressSpinner(false);
        },
          error => this.handleError(error)
        )
    }

  }


  // Build a lookup of contact ID -> name from the current autocomplete options.
  private _getContactNames(): { [id: string]: string } {
    const map: { [id: string]: string } = {};
    if (this.autoCompleteStudentContactOptions) {
      this.autoCompleteStudentContactOptions.forEach(c => {
        const id = c.id?.toString();
        if (id && !id.includes(',')) {
          map[id] = c.title;
        }
      });
    }
    return map;
  }

  studentIncrement(attendee: any) {
    if (!Array.isArray(this.field_student)) { this.field_student = [] }

    const contactNames = this._getContactNames();

    // If field_students is a comma-separated string (couple "Both" option), split into
    // individual IDs. Each gets its own attendee record with its own status.
    const studentIds = (attendee.field_students && typeof attendee.field_students === 'string' && attendee.field_students.includes(','))
      ? attendee.field_students.split(',')
      : [attendee.field_students];

    for (const studentId of studentIds) {
      this.field_student.push({
        ...attendee,
        field_students: [studentId],
        _displayName: contactNames[studentId?.toString()] || attendee.field_student_account,
        type: 'attendees',
        bundle: 'attendance',
        field_event_reference: this.entityId
      });
    }

    this.resetFieldStudent();
  }

  studentRemove = (index: number): void => {
    this.f.form.controls['__field_students_inline_form'].reset();
    this.editMode = false;
    this.field_student.splice(index, 1);

    this.resetFieldStudent();
  }

  studentUpdateLoad = (index: number): void => {
    LoggerService.log('UpdateLoad called...')
    this.resetFieldStudent();

    // Load the student enrollments again. Don't auto-select during edit mode.
    this.lookupStudentAccountContact({ option: { value: this.field_student[index].field_student_account } }, false, 'group_lesson');
    // Load the student contacts again.
    this.onStudentNameSelectThenLoadEnrollmentName({ option: { value: this.field_student[index].field_student_account } })

    // Append type & bundle to model.
    this.field_student[index]['type'] = 'attendees';
    this.field_student[index]['bundle'] = 'attendance';

    this.editIndex = index;
    this.editMode = true;

    // Wait for autoCompleteStudentContactOptions to be populated (async from API).
    // Once loaded, filter out the combined couple option. When editing an existing
    // attendee, the record represents a single student, so offering the "Both" option
    // would be misleading. Individual contact radio buttons are shown instead.
    const waitForOptions = () => {
      if (this.autoCompleteStudentContactOptions && this.autoCompleteStudentContactOptions.length > 0) {
        this.autoCompleteStudentContactOptions = this.autoCompleteStudentContactOptions
          .filter(c => !c['id'].toString().includes(','));

        // Keep the attendee id so the backend updates the existing attendees
        // entity in place instead of creating a new one on every edit.
        const formValue = { ...this.field_student[index] };

        // Convert array field_students back to string for radio button matching
        if (Array.isArray(formValue.field_students)) {
          formValue.field_students = formValue.field_students.join(',');
        }

        this.f.controls['__field_students_inline_form'].patchValue(formValue);
      } else {
        // Options not ready yet, try again in 100ms
        setTimeout(waitForOptions, 100);
      }
    };

    waitForOptions();
  }

  studentUpdateSubmit(_number: any, attendee: any) {
    const contactNames = this._getContactNames();

    // Convert comma-separated field_students back to array format for saving
    if (attendee.field_students && typeof attendee.field_students === 'string' && attendee.field_students.includes(',')) {
      attendee.field_students = attendee.field_students.split(',');
    }

    // Resolve display name from the selected contact
    const selectedId = Array.isArray(attendee.field_students) ? attendee.field_students[0] : attendee.field_students;
    attendee._displayName = contactNames[selectedId?.toString()] || attendee.field_student_account;
    attendee.field_event_reference = this.entityId;

    this.field_student[this.editIndex] = attendee;
    this.f.form.controls['__field_students_inline_form'].reset();
    this.editMode = false;

    this.resetFieldStudent();
  }

  resetFieldStudent() {
    this.enrollmentMessage = "";
    this.autoCompleteStudentContactOptions = null;
    this.enrollmentsForUser = null;
    this.f.form.controls['__field_students_inline_form'].reset();
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();
    // Ensure the inline form stays clean for adding new attendees in group lessons
    this.clearInlineForm();
  }

  clearInlineForm() {
    if (this.f && this.f.form.controls['__field_students_inline_form']) {
      this.f.form.controls['__field_students_inline_form'].reset();
      // Also clear any bound values that might repopulate
      setTimeout(() => {
        this.f.form.controls['__field_students_inline_form'].reset();
      }, 100);
    }
  }

  // Override the form setup to prevent existing attendee data from populating inline form
  override setFormValues() {
    super.setFormValues();
    // Clear the inline form after base form values are set
    this.clearInlineForm();
  }

  handleDateTimeChange(value: any) {
    if (value) {
      const momentDate = moment(value);
      this.onDateChange(momentDate);
      this.uiUpdateDate(momentDate);
    }
  }

  override onDeleteSubmit(form: any) {
    let id = this._injectedDialogData['data']['EntityID'];

    LoggerService.log('delete group lesson called...', id);

    this.displayProgressSpinner(true);
    this._entityRESTService.deleteEntity(this.eckType, this.bundle, id)
      .subscribe(data => {
        // Only close the current dialog, not all dialogs
        this.closeDialog();

        this.displayProgressSpinner(false);
      },
        error => this.handleError(error)
      );
  }

  // --- Recurring series methods (multi-instructor, attendee-less) ---

  onRecurrenceChanged(config: RecurrenceConfig | null): void {
    this.isRecurring = !!config;
    this.recurrenceConfig = config;
    this.seriesConflicts = [];
  }

  /** Human-readable instructor name for a uid, for tagging conflicts. */
  private instructorName(uid: string): string {
    const match = (this._fieldsService.instructorData() || []).find((i: any) => String(i.uid) === String(uid));
    return match?.value || `Instructor ${uid}`;
  }

  /** Selected instructor uids from the multi-select (always normalize to an array). */
  private selectedInstructorIds(form: any): string[] {
    const raw = form?.value?.field_instructor;
    if (Array.isArray(raw)) return raw.filter(Boolean);
    return raw ? [raw] : [];
  }

  /** Build save options for one instructor's series. Group lessons carry no student. */
  private buildSeriesSaveOptions(form: any, instructorId: string): StandingSeriesSaveOptions {
    const startDateObj = this.field_date_and_time ? new Date(this.field_date_and_time) : new Date();
    const startDate = `${startDateObj.getFullYear()}-${String(startDateObj.getMonth() + 1).padStart(2, '0')}-${String(startDateObj.getDate()).padStart(2, '0')}`;
    return {
      bundle: 'group_lesson',
      noun: 'group lessons',
      operatorId: instructorId,
      includeEnrollment: false,
      config: this.recurrenceConfig!,
      attendee: {},
      studentId: '',
      studioId: this._authService.selectedStudio?.id,
      time: this.field_date_and_time ? new Date(this.field_date_and_time).toTimeString().substring(0, 5) : '12:00',
      startDate,
      duration: form?.value?.field_duration || '00:45',
      typeId: form?.value?.field_type || '',
      notes: form?.value?.field_notes || '',
    };
  }

  private saveAsRecurring(form: any): void {
    if (this.isSaving) return;
    const instructorIds = this.selectedInstructorIds(form);
    if (instructorIds.length === 0) {
      this.errorMessage = 'Select at least one instructor.';
      return;
    }
    this.isSaving = true;
    this.seriesConflicts = [];
    // A re-save while conflicts are showing starts fresh series, so retire the
    // ones still pending from the previous attempt.
    for (const seriesId of this.pendingSeriesIds) {
      this._seriesSave.cancel(seriesId).subscribe(() => {}, () => {});
    }
    this.pendingSeriesIds = [];
    this._pendingOptsById.clear();

    const calls = instructorIds.map(uid => {
      const opts = this.buildSeriesSaveOptions(form, uid);
      return new Observable<{ uid: string; opts: StandingSeriesSaveOptions; result: StandingSeriesSaveResult }>(obs => {
        this._seriesSave.createAndGenerate(opts).subscribe(result => {
          obs.next({ uid, opts, result });
          obs.complete();
        });
      });
    });

    forkJoin(calls).subscribe(outcomes => this.applyRecurringOutcomes(outcomes));
  }

  generateIgnoringConflicts(): void {
    if (this.pendingSeriesIds.length === 0 || this.isSaving) return;
    this.isSaving = true;
    this.seriesConflicts = [];

    const calls = this.pendingSeriesIds.map(seriesId => {
      const opts = this._pendingOptsById.get(seriesId)!;
      return new Observable<{ uid: string; opts: StandingSeriesSaveOptions; result: StandingSeriesSaveResult }>(obs => {
        this._seriesSave.generateIgnoringConflicts(seriesId, opts).subscribe(result => {
          obs.next({ uid: opts.operatorId, opts, result });
          obs.complete();
        });
      });
    });

    forkJoin(calls).subscribe(outcomes => this.applyRecurringOutcomes(outcomes));
  }

  /** Aggregate per-instructor results: show conflicts tagged by instructor, else close on success. */
  private applyRecurringOutcomes(
    outcomes: { uid: string; opts: StandingSeriesSaveOptions; result: StandingSeriesSaveResult }[],
  ): void {
    const conflicts: any[] = [];
    const stillPending: number[] = [];
    let createdTotal = 0;
    let hadError = false;

    for (const { uid, opts, result } of outcomes) {
      if (result.kind === 'conflicts') {
        stillPending.push(result.seriesId);
        this._pendingOptsById.set(result.seriesId, opts);
        const name = this.instructorName(uid);
        for (const c of result.conflicts) {
          conflicts.push({ ...c, instructor: name });
        }
      } else if (result.kind === 'created') {
        createdTotal += result.count;
      } else {
        hadError = true;
        this.errorMessage = result.message;
      }
    }

    this.pendingSeriesIds = stillPending;

    if (conflicts.length > 0) {
      this.seriesConflicts = conflicts;
      this.isSaving = false;
      return;
    }

    this.isSaving = false;
    if (hadError) {
      return;
    }

    this._snackBar.open(
      `Created ${createdTotal} recurring group lesson${createdTotal === 1 ? '' : 's'}.`,
      'OK',
      { duration: 5000 },
    );
    this.closeDialog();
  }

  cancelRecurringSave(): void {
    for (const seriesId of this.pendingSeriesIds) {
      this._seriesSave.cancel(seriesId).subscribe(() => {}, () => {});
    }
    this.seriesConflicts = [];
    this.pendingSeriesIds = [];
    this._pendingOptsById.clear();
    this.isSaving = false;
  }
}

